import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  AlertTriangle, Loader2, LogIn, Maximize2, Minimize2, RotateCw,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { sportsGuestViewAllowed } from '../lib/sportsAccess';
import {
  PROVIDER_STORAGE_KEY, readSportsbookLink, resolveProvider,
} from '../lib/sportsbookProviders';
import AuthModal from '../components/AuthModal';
import SportsLoginGate from '../components/SportsLoginGate';
import useChatFreePage from '../hooks/useChatFreePage';
import './sportsbook-betby.css';

// Hazır spor kitabı çerçevesi — SynCode/Betby ve Flexrix için **tek** sayfa.
//
// Kendi sportsbook'umuzun (oran beslemesi + kupon + cashout) aksine burada
// **bahsin tamamı sağlayıcıdadır**: sayfa yalnızca bir çerçeve açar, para
// hareketi sağlayıcının seamless callback'iyle gelir
// (bkz. backend/routers/syncode_router.py, backend/routers/flexrix_router.py).
//
// Sağlayıcıya göre değişen tek şey launch ucudur; akış (giriş kontrolü,
// çerçeve, yavaş yükleme çıkışı, kabuğa uyum) aynıdır. İki ayrı sayfa
// tutulsaydı bir tarafta düzeltilen hata diğerinde kalırdı.
//
// Bahis yapıldığında bakiye sunucuda değişir; üstteki rakam onunla birlikte
// tazelenmeli, yoksa oyuncu "bahsim geçmedi" sanır. Tazeleme **burada
// yapılmaz**: `AuthContext` zaten 15 saniyede bir `/auth/me` sorar (sekme
// görünürken). Sayfanın kendi zamanlayıcısı hem isteği ikiye katlıyordu hem de
// `user` nesnesini baştan yazıyordu — aşağıdaki launch efekti nesnenin
// kimliğine bağlı olduğu için çerçeve her tazelemede yeniden açılıyor,
// oyuncunun doldurduğu kupon kayboluyordu.

// Sağlayıcı çerçevesi bu süre içinde yüklenmezse oyuncuya "tekrar dene" çıkışı
// sunulur; oturum iptal edilmez (GameModal'daki yaklaşımın aynısı).
const SLOW_LOAD_MS = 9000;

// SSO jetonu **tek kullanımlıktır ve adreste kalmaz**: sağlayıcının host
// sayfası `?token=`'ı bir kez bozdurup adresi kendi oturum parametreleriyle
// yeniden yazıyor, SPA da onları okuduktan sonra adresten siliyor ve oturumu
// yalnızca bellekte tutuyor (canlıda ölçüldü, 04.09.2026: exchange sonrası
// localStorage'da hiçbir oturum kaydı yok, kurulan çerezi SPA kullanmıyor).
// Sonuç: çerçeve **ikinci kez** yüklendiği anda oyuncu misafire düşüyor ve
// kupon çubuğunda "Bahis yapmak için lütfen GİRİŞ" yazıyor — site oturumu
// açık olduğu hâlde. İkinci yükleme bizden bağımsız da olur: mobilde sekme
// arka plandan dönünce, bellek baskısında ya da SPA kendini yenilediğinde.
//
// Bu yüzden beklenmedik her yeniden yükleme **taze jetonla** karşılanır. İlk
// açılışta iki yükleme normaldir (token'lı sayfa + sağlayıcının kendi
// yönlendirmesi), bu yüzden ölçü sayı değil zamandır: adres verildikten sonra
// bu süre geçmişken gelen bir yükleme artık ilk el sıkışmanın parçası
// değildir. İki yenileme arası alt sınır ise sağlayıcı sürekli yenilerse
// sonsuz döngüyü keser.
const FRESH_LOAD_MS = 20000;
const MIN_RELAUNCH_MS = 15000;

// Çerçevenin yüksekliği ölçülerek verilir. Sabit bir tahmin (eskiden 140px)
// hiçbir kurulumda tutmuyordu: kabuk temadan temaya farklıdır (Merit'te üst
// kategori barı, panelden gelen duyuru bandı, dar ekranda alt menü). Eksik
// hesaplanan kabuk sayfayı ikinci bir kaydırma çubuğuyla aşağı taşırıyor,
// spor kitabının altı ekranın dışında kalıyordu.
// Kabukların alt menüsü tek bir sınıfla işaretlenir (`app-bottom-bar`, tema
// sınıfının yanında durur). Liste bir süre kabuk kabuk sayılıyordu ve altıncı
// şema eklenirken **iki yerde birden** unutuldu: Vega'nın alt barı ne ölçüye
// giriyor ne tam ekranda gizleniyordu, bu yüzden telefonda çerçeve 56px fazla
// hesaplanıp sağlayıcının kupon çubuğu menünün arkasında kalıyordu ("Bahis
// Yap" düğmesi görünmüyordu). Yeni bir kabuk eklerken alt menüye bu sınıfı
// verin — seçici genişletilmez.
const BOTTOM_BAR_SELECTOR = '.app-bottom-bar';

// Kaydırmada ekrandan çekilebilen üst bar (bugün yalnızca Vega taşır, bkz.
// sportsbook-betby.css). Taşımayan kabukta başlık yapışkandır ve yerinde
// kalır; "kabuk gizlendi" demek orada yalandır.
const TOP_BAR_SELECTOR = '.app-top-bar';

// Oyuncunun son seçtiği sağlayıcı tarayıcıda durur; okuma bir kolaylıktır ve
// başarısız olabilir (gizli pencere, site verisi kapalı). Sayfa kayıtsız da
// doğru açılmalıdır: değer okunamazsa listenin ilki seçilir.
const storedProvider = () => {
  try {
    return window.localStorage.getItem(PROVIDER_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const rememberProvider = (id) => {
  try {
    window.localStorage.setItem(PROVIDER_STORAGE_KEY, id);
  } catch {
    /* tercih kaydedilemedi; sayfa yine çalışır */
  }
};

// CSS sınıfları tarihsel olarak `betby-` önekini taşır; dosya üç sağlayıcı
// tarafından paylaşılan çerçevenin biçimidir.
//
// `providers` yayında olan hazır spor kitaplarının listesidir (sunucu
// söyler). Birden çoksa çerçevenin üstünde logolu bir geçiş şeridi çizilir ve
// seçim oyuncunundur. `endpoint` tek sağlayıcılı eski çağrı biçimidir.
const SportsbookFrame = ({ providers, endpoint }) => {
  const { user } = useAuth();
  const settings = useSettings();
  const userId = user?.id;
  const list = useMemo(() => {
    if (providers?.length) return providers;
    return endpoint
      ? [{ id: '', label: 'Spor Bahisleri', logo: '', endpoint }]
      : [];
  }, [providers, endpoint]);
  // Ana sayfanın maç şeridi spor sayfasını belirli bir sağlayıcıyla ve
  // sayfasıyla açabilir (`?sb=sportdigi&sb_path=…`). Adresteki seçim kayıtlı
  // tercihin önündedir; derin bağlantı yalnızca ilk açılışta gönderilir —
  // sağlayıcının kendi yenilemesinde oyuncuyu maça geri götürmek, gezindiği
  // sayfadan koparmak olurdu.
  const [link] = useState(() => readSportsbookLink(
    typeof window === 'undefined' ? '' : window.location.search,
  ));
  const deepPathRef = useRef(link.path);
  const [wanted, setWanted] = useState(() => link.provider || storedProvider());
  // Kayıtlı seçim artık yayında olmayabilir (kurulum sağlayıcıyı panelden
  // kapatmış olabilir); o zaman sessizce listenin ilkine düşer — aksi hâlde
  // spor sayfası boş açılırdı.
  const active = resolveProvider(list, wanted);
  const activeId = active?.id || '';
  const activeEndpoint = active?.endpoint || '';
  const activeBook = active?.book || '';
  // Misafir görünümü: oturum yokken spor kitabı **izleme** modunda açılır —
  // oranlar görünür, kupon için giriş gerekir (kararı sağlayıcı verir: jeton
  // gönderilmez / bakiyesi sıfır bir hesap gönderilir). Panelden kapatılırsa
  // ziyaretçi giriş kartını görür. Ayar yanıtı gelmeden karar verilmez:
  // varsayılan açık olduğu için sayfa bir an "giriş yapın" demez.
  const guestView = !userId && sportsGuestViewAllowed(settings);
  const [url, setUrl] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState('');
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [slow, setSlow] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  // Spor kitabı ekranın kalanını doldurur ama kabuk (header + kategori barı +
  // duyuru bandı) dar ekranda geriye çok az yer bırakır. Tam ekran, çerçeveyi
  // kabuğun üstüne alır; tercih oyuncunundur, varsayılan kapalıdır.
  const [immersive, setImmersive] = useState(false);
  const slowTimer = useRef(null);
  const shellRef = useRef(null);
  // Üst kabuğun ölçülen yüksekliği; kaydırma eşiği odur (aşağıya bakın).
  const topRef = useRef(0);
  const chromeRef = useRef(-1);
  // Çerçeveye adresi verdiğimiz an ve son tazeleme anı — beklenen yüklemeyi
  // sağlayıcının kendi yenilemesinden ayırmak için (bkz. FRESH_LOAD_MS).
  const launchedAtRef = useRef(0);
  const relaunchedAtRef = useRef(0);

  // Sayfa sitenin kabuğu içinde açılır ama kabuğun dar genişlik sınırı
  // kaldırılır: spor kitabı kendi düzenini taşır (sports.css'teki `sb-page`
  // ile aynı gerekçe). `sb-frame-page` çerçeve açıkken üçüncü taraf
  // widget'larını kapatan CSS kuralının tutamağıdır.
  //
  // Sayfanın kaydırması **kilitlenmez**: bir süre kilitliydi (sitenin kaydırma
  // çubuğu sağlayıcınınkiyle yan yana düşünce oyuncu listeyi kaydıramıyordu)
  // ama telefonda dokunmatik kaydırmayı tümüyle kırıyor, masaüstünde de
  // footer'ı erişilmez yapıyordu. Çerçevenin dışı oyuncunundur.
  useEffect(() => {
    document.body.classList.add('sb-page', 'sb-frame-page');
    return () => {
      document.body.classList.remove('sb-page', 'sb-frame-page');
    };
  }, []);

  // Canlı destek düğmesi ekranın sağ alt köşesindedir ve sağlayıcının kupon
  // çubuğunu örter: oyuncu kuponu ve "Bahis yap" düğmesini göremiyordu. Çerçeve
  // açıkken gizlenir (GameModal'ın oyun açıkken yaptığının aynısı); destek
  // kabuğun kendi "Canlı Destek" düğmesinden açılmaya devam eder.
  //
  // Üç kapı da tek yerdedir (`useChatFreePage`): sitenin kendi LiveChat
  // widget'ı, panelden girilen özel koddan gelen üçüncü taraf widget'ı ve
  // ölçüye güvenmeyen `chat-free-page` CSS kuralı. Aynı ihtiyaç ödeme
  // ekranında da vardır; kuralı sayfa başına kopyalamayın.
  useChatFreePage();

  const launch = useCallback(async () => {
    setState('loading');
    setError('');
    setFrameLoaded(false);
    setSlow(false);
    try {
      const path = activeId && activeId === link.provider ? deepPathRef.current : '';
      const body = {};
      if (path) body.path = path;
      if (activeBook) body.book = activeBook;
      const { data } = await client.post(activeEndpoint, body);
      deepPathRef.current = '';
      launchedAtRef.current = Date.now();
      setUrl(data.launch_url);
      setState('ready');
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
        || 'Spor bahisleri şu anda açılamıyor. Lütfen daha sonra deneyin.',
      );
      setState('error');
    }
  }, [activeEndpoint, activeBook, activeId, link.provider]);

  // Adresle gelen seçim tarayıcıya da yazılır: oyuncu sonra menüden spor
  // sayfasını açtığında aynı sağlayıcıyı görmelidir.
  useEffect(() => {
    if (link.provider && activeId === link.provider) rememberProvider(activeId);
  }, [link.provider, activeId]);

  // Sağlayıcı geçişi: eski çerçevenin adresi **hemen** bırakılır. Yeni adres
  // gelene kadar tutulsaydı oyuncu bir sağlayıcıya basıp diğerinin sayfasına
  // bakmaya devam ederdi. Tercih tarayıcıya yazılır; sunucuya değil (bir
  // oyuncunun seçimi diğerininkini değiştirmemelidir).
  const selectProvider = useCallback((id) => {
    if (!id || id === activeId) return;
    rememberProvider(id);
    deepPathRef.current = '';
    setWanted(id);
    setUrl('');
    setState('loading');
    setFrameLoaded(false);
  }, [activeId]);

  // Çerçevenin her yüklenişi buraya düşer. Adresi biz vermişsek (ilk açılış ve
  // sağlayıcının hemen ardından yaptığı yönlendirme) yapılacak bir şey yok;
  // aradan zaman geçmişken gelen bir yükleme ise oturumu düşürmüştür — o
  // yükleme jetonu adreste bulamaz ve oyuncu misafir olarak açılır. Tek çare
  // taze bir jetonla yeniden açmaktır: jetonun ömrü 5 dakikadır, elde tutulan
  // adres ikinci kez kullanılamaz.
  const handleFrameLoad = useCallback(() => {
    setFrameLoaded(true);
    if (!url) return;
    // Misafir adresi jeton taşımaz: yeniden açmanın tazeleyeceği bir oturum
    // yoktur, sağlayıcının her yenilemesinde istek atmak boşuna olurdu.
    if (!userId) return;
    const now = Date.now();
    if (now - launchedAtRef.current < FRESH_LOAD_MS) return;
    if (now - relaunchedAtRef.current < MIN_RELAUNCH_MS) return;
    relaunchedAtRef.current = now;
    launch();
  }, [launch, url, userId]);

  // Tam ekranda kabuk arkada kalır; oyuncunun çıkışı düğme **ve** Esc'tir —
  // sağlayıcının SPA'sı odaktayken tuş bize hiç ulaşmaz, düğme tek güvenilir
  // yoldur ama alışkanlık olan tuşu da bırakmak ucuzdur.
  useEffect(() => {
    if (!immersive) return undefined;
    document.body.classList.add('sb-frame-immersive');
    const onKey = (event) => {
      if (event.key === 'Escape') setImmersive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('sb-frame-immersive');
      window.removeEventListener('keydown', onKey);
    };
  }, [immersive]);

  // Bağımlılık üyenin **kimliğidir**, `user` nesnesi değil: nesne arka plandaki
  // hesap tazelemesiyle saniyeler içinde yeniden kurulur ve nesneye bağlanan
  // efekt her tazelemede yeni bir launch isteği atıp iframe'in adresini
  // değiştiriyordu — sayfa kendi kendini yeniliyor, oyuncunun doldurduğu kupon
  // ve açtığı canlı maç sıfırlanıyordu.
  useEffect(() => {
    if (!userId && !guestView) {
      setState('idle');
      return;
    }
    launch();
  }, [userId, guestView, launch]);

  // Kabuğun kapladığı yer ölçülüp çerçeveye kalan yükseklik yazılır. Duyuru
  // bandı kapatılabildiği ve alt menü yalnızca dar ekranda çizildiği için ölçüm
  // bir kez değil, gövde her boyut değiştirdiğinde yapılır.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;
    // Ölçü elemana yazılır: mesaj kabuğundan çerçeveye geçildiğinde eleman
    // değişir ve son değer yeni elemanda yoktur.
    chromeRef.current = -1;

    const apply = () => {
      // Telefonda sayfa kaydırılabilir (kilit yalnızca masaüstünde uygulanır):
      // kaydırılmış hâlde ölçülen `top` kabuğun kapladığı yeri değil ekranın o
      // anki hâlini söyler ve çerçeve olduğundan uzun hesaplanırdı. Mobil adres
      // çubuğu gizlenirken `resize` tam da bu anda tetiklenir, bu yüzden ölçüm
      // kaydırılmışken atlanır — son geçerli değer korunur.
      if ((window.scrollY || window.pageYOffset || 0) > 0) return;
      const top = Math.max(0, Math.round(shell.getBoundingClientRect().top));
      // Gizli alt menü (`display: none`) sıfır yükseklikli bir dikdörtgen
      // döndürür; ölçüyü onun üzerinden almak bütün ekranı kabuk sayardı.
      // Arama çoğuldur: bir kabuk ortak barı DOM'da bırakıp CSS ile
      // gizleyebilir (Vega'daki `.mobile-bottom-nav` kuralı) ve tekil bir
      // sorgu gizli olanı yakalarsa alt menü hiç sayılmamış olurdu.
      let bottom = 0;
      document.querySelectorAll(BOTTOM_BAR_SELECTOR).forEach((bar) => {
        const rect = bar.getBoundingClientRect();
        if (!rect.height) return;
        bottom = Math.max(bottom, Math.round(window.innerHeight - rect.top));
      });
      bottom = Math.max(0, bottom);
      const chrome = top + bottom;
      // Üst kabuk ayrıca saklanır: kaydırma onu ekrandan çektiğinde çerçeve
      // yalnızca alt menü kadar kısalmalıdır (aşağıdaki efekt).
      topRef.current = top;
      // Değişmeyen değeri yeniden yazmak ResizeObserver'ı döngüye sokardı:
      // yazma gövdenin boyutunu değiştirir, o da ölçümü tetikler.
      if (chrome === chromeRef.current) return;
      chromeRef.current = chrome;
      shell.style.setProperty('--betby-chrome', chrome + 'px');
      shell.style.setProperty('--betby-chrome-bottom', bottom + 'px');
    };

    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(apply)
      : null;
    observer?.observe(document.body);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      observer?.disconnect();
    };
  }, [state, userId, guestView, immersive]);

  // Kaydırma üst kabuğu ekrandan çeker: oyuncu sayfayı aşağı ittiğinde spor
  // kitabı ekranın tamamına yayılır, geri döndüğünde menü yerine gelir.
  // Eşik **ölçülen kabuk yüksekliğidir**, sabit bir sayı değil: kabuk temadan
  // temaya farklıdır ve eşiği erken tutmak çerçeveyi kaydırmanın ortasında
  // büyütüp altını alt menünün arkasına sokardı. Tam eşikte kabuk zaten
  // yukarı çıkmıştır, çerçeve büyüdüğünde ekranın tepesine oturur — bu yüzden
  // ne kesilme ne titreme olur (çerçevenin uzaması sayfayı aynı miktarda
  // uzatır, ölçü geri sıçramaz).
  //
  // **Yalnızca üst barı çekilebilen kabukta** (`app-top-bar`). Sınıf bir süre
  // her şemada yazılıyordu: Betrun gibi yapışkan başlıklı kabukta başlık
  // yerinde kalırken çerçeve kabuğun yüksekliği kadar uzuyor, altı ekranın
  // dışına taşıyor ve sayfa uzadığı için çerçeve kaydırmayla birlikte
  // "yürüyordu" (Betrun, 27.09.2026 — "duyuru bandı gidince yükseklik
  // artıyor"). O kabuklarda çerçevenin yüksekliği kaydırmadan bağımsızdır.
  useEffect(() => {
    // Tam ekranda kabuk zaten çizilmiyor; sınıfı orada da yazmak anlamsız.
    if (state !== 'ready' || immersive) return undefined;
    if (!document.querySelector(TOP_BAR_SELECTOR)) return undefined;
    const onScroll = () => {
      const top = topRef.current;
      const y = window.scrollY || window.pageYOffset || 0;
      document.body.classList.toggle('sb-chrome-hidden', top > 0 && y >= top);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('sb-chrome-hidden');
    };
  }, [state, immersive]);

  useEffect(() => {
    if (state !== 'ready' || frameLoaded) return undefined;
    slowTimer.current = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(slowTimer.current);
  }, [state, frameLoaded]);

  // Misafir görünümü panelden kapatılmış: kart ortak bileşendir — sitenin
  // kendi sportsbook'u da kapalıyken aynı şeyi gösterir.
  if (!user && !guestView) {
    return <SportsLoginGate />;
  }

  if (state === 'error') {
    return (
      <div className="betby-shell betby-shell--message" ref={shellRef}>
        <div className="betby-card">
          <AlertTriangle size={28} className="betby-card-icon betby-card-icon--warn" />
          <h1>Spor bahisleri açılamadı</h1>
          <p>{error}</p>
          <button type="button" className="betby-btn" onClick={launch}>
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`betby-shell${immersive ? ' betby-shell--immersive' : ''}`}
      ref={shellRef}
    >
      {/* Şerit bilinçli olarak incedir ve çerçevenin dışında durur: sağlayıcının
          kendi başlığının üstüne binen bir katman kupon çubuğunu örterdi
          (canlı destek düğmesindeki gerekçenin aynısı). */}
      <div className="betby-toolbar">
        {/* Birden çok hazır spor kitabı yayındaysa oyuncu buradan geçiş
            yapar. Şerit çerçevenin **dışındadır**: sağlayıcının kendi
            başlığının üstüne binen bir katman kupon çubuğunu örterdi.
            Logo panelden yüklenir ve göreli bir adres olabilir
            (`/api/uploads/...`), bu yüzden `resolveApiAssetUrl`'den geçer —
            ayrı origin'li kurulumda çözülmeyen adres 404 olur ve logo
            sessizce kaybolurdu. */}
        {list.length > 1 ? (
          <div className="betby-providers" role="tablist" aria-label="Spor kitabı">
            {list.map((row) => (
              <button
                key={row.id}
                type="button"
                role="tab"
                aria-selected={row.id === activeId}
                className={`betby-provider${row.id === activeId ? ' is-active' : ''}`}
                onClick={() => selectProvider(row.id)}
                title={row.label}
              >
                {row.logo ? (
                  <img
                    src={resolveApiAssetUrl(row.logo)}
                    alt={row.label}
                    className="betby-provider-logo"
                  />
                ) : (
                  <span className="betby-provider-name">{row.label}</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          // Tek sağlayıcı yayındayken şerit bir seçim değil başlıktır:
          // sağlayıcının markasını yazmak oyuncuya seçilecek bir şey varmış
          // gibi görünürdü. Panelden verilen ad yalnızca geçiş şeridinde
          // kullanılır.
          <span className="betby-toolbar-title">Spor Bahisleri</span>
        )}
        <div className="betby-toolbar-actions">
          {/* Misafir oranları görür ama kupon yapamaz. Giriş çağrısı bizim
              şeridimizde durur: sağlayıcının kendi "GİRİŞ" düğmesi çerçevenin
              içindedir ve site oturumunu açamaz. */}
          {!user && (
            <button
              type="button"
              className="betby-tool betby-tool--accent"
              onClick={() => setAuthOpen(true)}
              title="Bahis yapmak için giriş yapın"
            >
              <LogIn size={15} />
              <span className="betby-tool-label">Giriş Yap</span>
            </button>
          )}
          <button
            type="button"
            className="betby-tool"
            onClick={() => setImmersive((value) => !value)}
            title={immersive ? 'Site görünümüne dön' : 'Tam ekranda aç'}
          >
            {immersive ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            <span className="betby-tool-label">
              {immersive ? 'Küçült' : 'Tam ekran'}
            </span>
          </button>
        </div>
      </div>
      {(state === 'loading' || !frameLoaded) && (
        <div className="betby-overlay">
          <Loader2 size={26} className="betby-spin" />
          <span>Spor bahisleri yükleniyor…</span>
          {/* Çerçeve makul sürede açılmadıysa oyuncuya bir çıkış bırakılır:
              oturum iptal edilmez, yalnızca yeniden denemesi önerilir. Eskiden
              buradaki çıkış "yeni sekmede aç"tı — spor kitabı sitenin içinde
              kalmalı, ikinci bir pencere oyuncuyu siteden koparıyordu. */}
          {slow && (
            <button type="button" className="betby-btn betby-btn--ghost" onClick={launch}>
              <RotateCw size={14} />
              Tekrar dene
            </button>
          )}
        </div>
      )}
      {/* `allow-storage-access-by-user-activation` olmadan sağlayıcının sayfası
          Storage Access API'yi çağıramaz: üçüncü taraf çerezleri kısıtlı
          tarayıcıda konsola "requestStorageAccess: Refused to execute request.
          The document is sandboxed" düşer ve sağlayıcının reCAPTCHA'sı ile
          oturum deposu sessizce kapalı kalır (canlıda ölçüldü, 04.09.2026). */}
      {url && (
        <iframe
          src={url}
          title="Spor Bahisleri"
          className="betby-frame"
          allow="fullscreen; clipboard-write; encrypted-media"
          sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin allow-downloads allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
          onLoad={handleFrameLoad}
        />
      )}
      {!user && (
        <AuthModal
          open={authOpen}
          onClose={() => setAuthOpen(false)}
          defaultTab="login"
        />
      )}
    </div>
  );
};

export default SportsbookFrame;
